import React, { useState, createRef, useEffect } from 'react';
import { Map, GeoJSON } from 'react-leaflet';
import phuBoundaries from '../../phuBoundariesSmall.json';
import dict from '../../dictionary';
import cData from '../../reducedData.json';
import FuzySearch from '../fuzySearch/fuzySearch';
import { getPopupContent } from './Popup';
import { lerp } from '../utils';
import Callout from '../callout/Callout';
import 'leaflet/dist/leaflet.css';
import './regionalMap.scss';
import HeatmapLegend from './HeatmapLegend';
import useInterval from './useInterval';
import trans from '../../translations.json';
import Loading from '../loading/loading';
import ChartMetric from './ChartMetric';
import CvizRadioGroup from '../cvizRadioGroup/cvizRadioGroup';
import SearchResultsDropdown from '../search/searchResultDropdown';

const citiesPHU = Object.keys(cData.dataPHU).reduce(
  (sum, currPHU) => {
    return sum.concat(
      cData.dataPHU[currPHU].cities.map((city) => {
        return {
          city_name: city,
          phu_name: cData.dataPHU[currPHU][dict.Reporting_PHU],
        };
      }),
    );
  },
  [],
);

// const highestPhu = Object.values({ ...cData.dataPHU }).sort(
//   (a, b) => b[dict.PHU_pop] - a[dict.PHU_pop],
// )[0];

//console.log('highestPhu', highestPhu);

//Fsa disabled
const fsaArr = false;
// const fsaArr = Object.keys(cData.dataFSA).reduce((sum, currFsa) => {
//   if (
//     currFsa === 'maxTotal' ||
//     currFsa === 'maxCapita' ||
//     currFsa === 'Unknown FSA'
//   ) {
//     return sum;
//   }

//   return sum.concat([{ [dict.FSA_name]: currFsa }]);
// }, []);

const defaultMapCfg = {
  lat: 44.61,
  lng: -79.42,
  zoom: 6,
};

const mapColors = {
  [dict.totaCases]: {
    start: [219, 190, 226],
    end: [144, 71, 160],
  },
  [dict.NotResolved]: {
    start: [228, 190, 188],
    end: [164, 73, 67],
  },
  [dict.resolved]: {
    start: [184, 217, 180],
    end: [79, 140, 71],
  },
  [dict.deaths]: {
    start: [184, 184, 184],
    end: [83, 83, 83],
  },
  [dict.new]: {
    start: [178, 203, 239],
    end: [41, 102, 190],
  },
};

const searchOpts = {
  [dict.phu]: {
    includeMatches: true,
    keys: ['city_name', 'phu_name'],
  },
  [dict.fsa]: {
    includeMatches: true,
    keys: [dict.FSA_name],
    findAllMatches: true,
  },
};
const RegionalMap = (props) => {
  /**
   * A bit of badly written terminology used in this component
   * =====================
   * isPerCapita -> true/false. Whether the map is in a /100k view or not.
   *
   * metric -> dict.NotResolved/dict.resolved/dict.deaths/dict.totaCases. What the map measures the virus by.
   *
   * mode -> dict.phu/dict.fsa. Set of geometric polygons describing the area of effect.
   */

  // keep track of how to display and calculate the map
  const [isPerCapita, setIsPerCapita] = useState(true);
  const [metric, setMetric] = useState(dict.new);
  const [mode, setMode] = useState(dict.phu); // either phu or fsa

  // gatekeepers that clean input before actually sending it to their actual values
  const [
    intentionToTransferMode,
    setIntentionToTransferMode,
  ] = useState();
  const [
    intentionToTransferMetric,
    setIntentionToTransferMetric,
  ] = useState(dict.new);

  // store geojson bounds for both maps
  const [bounds, setBounds] = useState({});
  const [postalBounds, setPostalBounds] = useState({});

  // if the fsa geojson has finished downloading
  const [fsaReady, setFsaReady] = useState(false);

  // display
  const [mapColor, setMapColor] = useState(
    mapColors[dict.NotResolved],
  );
  const [showCallout, setShowCallout] = useState(false);

  // search
  const [searchResults, setSearchResults] = useState([]);
  const [waitingForSearch, setWaitingForSearch] = useState(false);

  // overwriting fuzzy search query
  const [searchQuery, setSearchQuery] = useState('');
  const [searchQueryTime, setSearchQueryTime] = useState(+new Date());

  // leaflet
  const mapRef = createRef();

  const resetMapView = () => {
    mapRef.current.leafletElement.flyTo(
      [defaultMapCfg.lat, defaultMapCfg.lng],
      defaultMapCfg.zoom,
      { animate: true, duration: 1 },
    );
  };
  /**
   * Gets the shade that a phu feature should be filled with based on current state.
   * @param {string} featureName Full name of Feature (phu/fsa)
   */
  const getShade = (featureName) => {
    let perc;
    const capitaMode = isPerCapita ? 'capita' : 'total';
    if (mode === dict.phu) {
      // PHU
      const phuData = cData.dataPHU[featureName];
      // console.log('cData', cData);
      if (metric === dict.totaCases) {
        // add up stuff client side for total cases
        perc =
          (phuData[dict.NotResolved] +
            phuData[dict.resolved] +
            phuData[dict.deaths]) /
          cData.maxStats[dict.phu][capitaMode][metric];
      } else {
        //otherwise take it directly from records
        perc =
          phuData[metric] /
          cData.maxStats[dict.phu][capitaMode][metric];
      }

      //console.log('capitaMode', capitaMode);

      if (isPerCapita) {
        perc /= phuData[dict.PHU_pop];
      }
    } else {
      // FSA
      const fsaData = cData.dataFSA[featureName];

      try {
        perc =
          fsaData[isPerCapita ? dict.FSA_per_100k : dict.FSA_active] /
          cData.maxStats[dict.fsa][capitaMode][metric];
      } catch (err) {
        perc = 0;
      }
    }

    return `rgb(${lerp(
      mapColor.start,
      mapColor.end,
      perc,
    ).toString()})`;
  };
  const onClickSearchResult = (phu_name) => {
    setSearchResults([]);
    setSearchQuery(phu_name);
    setSearchQueryTime(+Date.now());
    selectZone(phu_name);
  };
  //sync colors
  useEffect(() => {
    setMapColor(mapColors[metric]);
  }, [metric]);

  const hideDropdown = () => {
    setSearchResults([]);
  };

  useEffect(() => {
    if (
      intentionToTransferMetric !== dict.NotResolved &&
      (intentionToTransferMode === dict.fsa || mode === dict.fsa)
    ) {
      // redirect to phu mode
      setMode(dict.phu);
      setMetric(intentionToTransferMetric);

      resetMapView();

      // clear search in fuzzysearch to prevent loop
      setSearchQuery('');
      setSearchQueryTime(+new Date());

      // since search got cleared, remove results as well
      setSearchResults([]);

      //show callout
      setShowCallout(true);
    } else {
      setShowCallout(false);
      if (intentionToTransferMetric)
        setMetric(intentionToTransferMetric);
    }
  }, [intentionToTransferMetric, intentionToTransferMode]);

  const geoJSONStyle = (feature) => {
    let featureName;

    if (mode === dict.phu) {
      featureName = feature.properties[dict.PHU_NAME_ENG];
    } else {
      featureName = feature.properties[dict.FSA_ID];
    }
    const config = {
      color: '#fff',
      weight: 1,
      fillOpacity: 1,
      fillColor: getShade(featureName),
    };

    return config;
  };

  // dynamically change the popup content based on if it is per capita or not
  useEffect(() => {
    if (mode === dict.phu) {
      Object.keys(bounds).map((phu) => {
        bounds[phu].setPopupContent(
          getPopupContent(phu, props.lang, mode, isPerCapita),
        );
      });
    }
  }, [isPerCapita]);

  // if trying to transfer to fsa, but the data has not been downloaded,
  // keep polling until it's possible
  useInterval(
    () => {
      if (intentionToTransferMode === dict.fsa && !fsaReady) {
        return;
      } else {
        setMode(intentionToTransferMode);
        setIntentionToTransferMode();
      }
    },
    intentionToTransferMode ? 500 : null,
  );

  /**
   * Runs for every feature on the map
   */
  const onEachFeature = (feature, layer) => {
    let featureName;

    if (mode === dict.phu) {
      featureName = feature.properties[dict.PHU_NAME_ENG];
    } else {
      featureName = feature.properties[dict.FSA_ID];
    }

    // append the bounds of the phu to state
    if (mode === dict.phu) {
      setBounds((oldBounds) => {
        return { ...oldBounds, [featureName]: layer };
      });
    } else {
      setPostalBounds((oldBounds) => {
        return { ...oldBounds, [featureName]: layer };
      });
    }

    const popupContent = getPopupContent(
      featureName,
      props.lang,
      mode,
      isPerCapita,
    );
    layer.bindPopup(popupContent, { maxHeight: 'none' });
  };

  /**
   * Zooms in and map autofits to a GEOJSON object
   * @param {string} featureName PHU to zoom to
   */
  const selectZone = (featureName) => {
    const map = mapRef.current.leafletElement;

    const fitTo =
      mode === dict.phu
        ? bounds[featureName]
        : postalBounds[featureName];
    map.fitBounds(fitTo.getBounds().pad(0.85));
    setTimeout(() => {
      fitTo.openPopup();
    }, 500);
  };

  /**
   * Sets search data state after Fuze returns a new search
   */
  const searchResultsCallback = (query, results) => {
    if (query.length < 3) {
      if (searchResults !== []) {
        setSearchResults([]);
      }
      return;
    }

    setWaitingForSearch(false);

    setSearchResults(() => {
      return results
        ? results.slice(0, Math.min(results.length, 3))
        : [];
    });
  };

  let message = '';
  function getMessage(metric) {
    if (metric === dict.totaCases) {
      message = trans.heatmap.searchTotal[props.lang];
    } else if (metric === dict.NotResolved) {
      message = trans.heatmap.searchActive[props.lang];
    } else if (metric === dict.resolved) {
      message = trans.heatmap.searchResolved[props.lang];
    } else if (metric === dict.deaths) {
      message = trans.heatmap.searchDeaths[props.lang];
    } else if (metric === dict.new) {
      message = trans.heatmap.searchNew[props.lang];
    }

    return message;
  }

  return (
    <div
      id="viz-region-heatmap"
      className="ontario-row ontario-margin-bottom-32-!"
    >
      {props.showSidebar ? (
        <div
          id="compare-chart-controls"
          className="ontario-columns ontario-medium-4 ontario-small-12 ontario-margin-bottom-32-!"
        >
          {!props.hideChartMetric && (
            <ChartMetric
              lang={props.lang}
              setMetric={setIntentionToTransferMetric}
              metric={metric}
              mode={mode}
            />
          )}

          {showCallout && (
            <Callout
              className="ontario-margin-bottom-32-!"
              data={{
                type: 'informational',
                title: (metric === dict.totaCases
                  ? trans.heatmap.totalOnlyPhu
                  : metric === dict.resolved
                  ? trans.heatmap.resolvedOnlyPhu
                  : trans.heatmap.deathsOnlyPhu)[props.lang],
                message: trans.heatmap.pleaseSearchByCity[props.lang],
              }}
            />
          )}

          {/* citiesPHU.length && fsaArr.length */}

          {citiesPHU.length && (
            <>
              <div className="ontario-margin-bottom-8-!">
                <div className="ontario-label ontario-margin-bottom-8-!">
                  {!props.fuzzySearchProp
                    ? (metric === dict.NotResolved
                        ? trans.heatmap.showByPhuOrPostal
                        : trans.heatmap.showByPhu)[props.lang]
                    : props.fuzzySearchProp.title}
                </div>
                {!props.fuzzySearchProp && (
                  <div className="ontario-margin-bottom-32-!">
                    {getMessage(metric)}
                    {/* {trans.overview.Enter[props.lang]}{' '}
                    {trans.heatmap.firstBullet[props.lang]} */}
                  </div>
                )}
              </div>
              <FuzySearch
                onBlur={() =>
                  setTimeout(() => setSearchResults([]), 200)
                }
                searchQuery={searchQuery}
                searchQueryTime={searchQueryTime}
                searchResultsCallback={searchResultsCallback}
                data={mode === dict.phu ? citiesPHU : fsaArr}
                minSearchLength={3}
                searchSettings={searchOpts[mode]}
                searchResultsStartCallback={() => {
                  setWaitingForSearch(true);
                }}
                searchLabel={trans.heatmap.city[props.lang]}
                class="ontario-label"
              />
            </>
          )}
          <SearchResultsDropdown
            results={searchResults}
            handleOnClick={onClickSearchResult}
            hideDropdown={hideDropdown}
          />
          <CvizRadioGroup
            onChange={setIsPerCapita}
            className={
              'cviz-radioGroup-noflex ontario-margin-bottom-0-!'
            }
            optionsName={'capital'}
            value={isPerCapita}
            cases={[
              [true, trans.overview.per100K[props.lang]],
              [false, trans.general.numberOfCases[props.lang]],
            ]}
          />
          <a
            className="ontario-margin-bottom-32-!"
            href="#time-lag"
            rel="noopenner"
          >
            {trans.heatmap.whyNumbersDontMatch[props.lang]}
          </a>
        </div>
      ) : null}
      <div
        id="regionalMap"
        className="ontario-columns ontario-medium-8 ontario-small-12"
      >
        <Map
          aria-hidden="true"
          attributionControl={false}
          center={[defaultMapCfg.lat, defaultMapCfg.lng]}
          zoom={defaultMapCfg.zoom}
          minZoom={4}
          scrollWheelZoom={false}
          ref={mapRef}
          className="leaflet-container"
          closePopupOnClick={true}
          style={{ background: '#fff' }}
        >
          {mode === dict.phu && !intentionToTransferMode && (
            <GeoJSON
              data={phuBoundaries}
              style={geoJSONStyle}
              onEachFeature={onEachFeature}
            />
          )}

          {intentionToTransferMode && (
            <div className="covid-loader">
              <Loading />
            </div>
          )}

          {/* FSA Data */}
          {mode === dict.fsa && !intentionToTransferMode && (
            <GeoJSON
              data={postalBounds}
              style={geoJSONStyle}
              onEachFeature={onEachFeature}
            />
          )}
        </Map>
        <HeatmapLegend
          lang={props.lang}
          startColor={mapColor.start}
          endColor={mapColor.end}
          isPerCapita={isPerCapita}
          toPrecision={isPerCapita ? 2 : 2}
          maxNum={
            isPerCapita
              ? // cData.maxStats[mode]['capita'][metric].toFixed(2)
                cData.maxStats[mode]['capita'][metric] * 100000
              : cData.maxStats[mode]['total'][metric]

            // cData.maxStats[mode][isPerCapita ? 'capita' : 'total'][
            //   metric
            // ]
          }
        />
      </div>
    </div>
  );
};

export default RegionalMap;
